import React, { useEffect, useMemo, useState } from "react";
import Select from "react-select";
import '../../../node_modules/bootstrap/dist/css/bootstrap.min.css';
import TableContainer from '../../components/Common/TableContainer';
import * as Yup from "yup";
import { useFormik } from "formik";

//import components
import Breadcrumbs from '../../components/Common/Breadcrumb';
import DeleteModal from '../../components/Common/DeleteModal';

import {
    getFeatures as onGetFeatures,
    getActions as onGetActions,
    addNewFeature as onAddNewFeature,
    updateFeature as onUpdateFeature,
    deleteFeature as onDeleteFeature,
} from "store/actions";

//redux
import { useSelector, useDispatch } from "react-redux";
import { createSelector } from "reselect";

import {
    Col,
    Row,
    UncontrolledTooltip,
    Modal,
    ModalHeader,
    ModalBody,
    Form,
    Input,
    FormFeedback,
    Label,
    Card,
    CardBody,
    Button,
} from "reactstrap";
import Spinners from "components/Common/Spinner";
import { ToastContainer } from "react-toastify";
import { Link } from "react-router-dom";

const FEATURES = [
    {value: "User", label: "Utilisateur"},
    {value: "ClassRoom", label: "Classe"}, 
    {value: "Branch", label: "Branche"}, 
    {value: "Structure", label: "Structure"},
    {value: "Teacher", label: "Professeur"},
    {value: "Evaluation", label: "Évaluation"}, 
     {value: "JuryMember", label: "Membre de jury"}, 
];

const Feature = () => {
    document.title = "Fonctionnalités | CinzanEdu";

    const [modal, setModal] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const [feature, setFeature] = useState(null);
    const [selectedActions, setSelectedActions] = useState([]);

    const dispatch = useDispatch();

    // SELECTORS
    const FeatureProperties = createSelector(
        (state) => state.featureReducer, 
        (featureReducer) => ({
            features: featureReducer.features,
            loading: featureReducer.loading
        })
    );
    const {features, loading} = useSelector(FeatureProperties);
    const [isLoading, setLoading] = useState(loading);

    const ActionProperties = createSelector(
        (state) => state.actionReducer, 
        (actionReducer) => ({
            actions: actionReducer.actions,
            loading: actionReducer.loading
        })
    );
    const {actions} = useSelector(ActionProperties);

    // Load features + actions
    useEffect(() => {
        if (features && !features.length) dispatch(onGetFeatures());
        if (actions && !actions.length) dispatch(onGetActions());
    }, [dispatch, features, actions]);

    // Build react-select options from actions list
    const actionOptions = (actions || []).map(a => ({
        value: a.id,
        label: a.name + (a.code ? ` (${a.code})` : "")
    }));

    // validation
    const validation = useFormik({
        enableReinitialize: true,
        initialValues: {
            uid: (feature && feature.uid) || '',
            name: (feature && feature.name) || '',
            code: (feature && feature.code) || '',
            description: (feature && feature.description) || '',
        },
        validationSchema: Yup.object({
            name: Yup.string().required("Veuillez saisir le nom"),
            code: Yup.string().required("Veuillez sélectionner la classe"),
        }),
        onSubmit: (values) => {
            const payload = {
               
                uid: values.uid,
                name: values.name,
                code: values.code,
                description: values.description,
                
                action_ids: selectedActions.map(a => a.value)
            };

            if (isEdit) {
                // For update we include uid or id depending on your API
                dispatch(onUpdateFeature(payload));
            } else {
                dispatch(onAddNewFeature(payload));
            }
            validation.resetForm();
            setSelectedActions([]);
            toggle();
        },
    });

    const toggle = () => {
        if (modal) {
            setModal(false);
            setFeature(null);
            setSelectedActions([]);
        } else {
            setModal(true);
        }
    };

    const handleFeatureClick = item => {
        // item is a feature object from the table. Expect it to include permissions with action ids.
        setFeature({
            uid: item.uid,
            name: item.name,
            code: item.code,
            description: item.description,
        });

        // Pre-select actions:
        // The table's feature object should ideally include permissions with action objects:
        // feature.permissions = [{ action: { id, name, code } }, ...]
        // If your API returns feature.actions directly, adapt accordingly.
        /*if (item.permissions && Array.isArray(item.permissions)) {
            const pre = item.permissions
                .filter(p => p.action)
                .map(p => ({ value: p.action.id, label: p.action.name + (p.action.code ? ` (${p.action.code})` : "") }));
            setSelectedActions(pre);
        } else if (item.actions && Array.isArray(item.actions)) {
            const pre = item.actions.map(a => ({ value: a.id, label: a.name + (a.code ? ` (${a.code})` : "") }));
            setSelectedActions(pre);
        } else {
            // fallback: clear selection
            setSelectedActions([]);
        }
        */

        if (item.feature_permissions && Array.isArray(item.feature_permissions)) {
            const pre = item.feature_permissions.map(a => ({
                value: a.id,
                label: a.name + (a.code ? ` (${a.code})` : "")
            }));
            setSelectedActions(pre);
        } else if (item.feature_actions && Array.isArray(item.feature_actions)) {
            const pre = item.feature_actions.map(a => ({
                value: a.id,
                label: a.name + (a.code ? ` (${a.code})` : "")
            }));
            setSelectedActions(pre);
        } else {
            // fallback: clear selection
            setSelectedActions([]);
        }


        setIsEdit(true);
        toggle();
    };

    //delete feature
    const [deleteModal, setDeleteModal] = useState(false);
    const onClickDelete = (f) => {
        setFeature(f);
        setDeleteModal(true);
    };
    const handleDeletefeature = () => {
        if (feature && feature.uid) {
            dispatch(onDeleteFeature(feature.uid));
            setDeleteModal(false);
        }
    };

    const onClickNewFeature = () => {
        setIsEdit(false);
        setFeature(null);
        setSelectedActions([]);
        toggle();
    };

    const columns = useMemo(
        () => [
            { header: 'ID', accessorKey: "id", enableColumnFilter: false, enableSorting: true,
                cell: (cellProps) => <Link to="#" className="text-body fw-bold">{cellProps.row.original.id}</Link>
            },
            { header: "Nom", accessorKey: "name", enableColumnFilter: false, enableSorting: true },
            { header: "Code", accessorKey: "code", enableColumnFilter: false, enableSorting: true },
            {
  header: "Permissions",
  accessorKey: "feature_permissions",
  enableColumnFilter: false, enableSorting: true,
  cell: info =>
    Array.isArray(info.getValue())
      ? info.getValue().map(a => a.name).join(", ")
      : ""
},

            {
                header: 'Action',
                enableColumnFilter: false,
                enableSorting: true,
                cell: (cellProps) => {
                    return (
                        <ul className="list-unstyled hstack gap-1 mb-0">
                            <li>
                                <Link to="#"
                                    className="btn btn-sm btn-soft-info"
                                    onClick={() => handleFeatureClick(cellProps.row.original)}
                                    id={`edittooltip-${cellProps.row.original.id}`}>
                                    <i className="mdi mdi-pencil-outline" />
                                    <UncontrolledTooltip placement="top" target={`edittooltip-${cellProps.row.original.id}`} >Edit</UncontrolledTooltip>
                                </Link>
                            </li>
                            <li>
                                <Link to="#"
                                    className="btn btn-sm btn-soft-danger"
                                    onClick={() => onClickDelete(cellProps.row.original)}
                                    id={`deletetooltip-${cellProps.row.original.id}`}>
                                    <i className="mdi mdi-delete-outline" />
                                    <UncontrolledTooltip placement="top" target={`deletetooltip-${cellProps.row.original.id}`}>
                                        Delete
                                    </UncontrolledTooltip>
                                </Link>
                            </li>
                        </ul>
                    );
                }
            },
        ],
        []
    );

    return (
        <React.Fragment>
            <DeleteModal
                show={deleteModal}
                onDeleteClick={handleDeletefeature}
                onCloseClick={() => setDeleteModal(false)}
            />
            <div className="page-content">
                <div className="container-fluid">
                    <Breadcrumbs title="Fonctionnalités" breadcrumbItem="Liste des fonctionnalités" />
                    {
                        isLoading ? <Spinners setLoading={setLoading} />
                            :
                            <Row>
                                <Col lg="12">
                                    <Card>
                                        <CardBody className="border-bottom">
                                            <div className="d-flex align-items-center">
                                                <h5 className="mb-0 card-title flex-grow-1"></h5>
                                                <div className="flex-shrink-0">
                                                    <Link to="#!" onClick={onClickNewFeature} className="btn btn-primary me-1">Ajouter une fonctionnalité</Link>
                                                </div>
                                            </div>
                                        </CardBody>
                                        <CardBody>
                                            <TableContainer
                                                columns={columns}
                                                data={features || []}
                                                isCustomPageSize={true}
                                                isGlobalFilter={true}
                                                isPagination={true}
                                                SearchPlaceholder="Rechercher ..."
                                                tableClass="align-middle table-nowrap dt-responsive nowrap w-100 table-check dataTable no-footer dtr-inline"
                                                theadClass="table-light"
                                                pagination="pagination"
                                                paginationWrapper="dataTables_paginate paging_simple_numbers pagination-rounded"
                                            />
                                        </CardBody>
                                    </Card>
                                </Col>
                            </Row>
                    }
                    <Modal isOpen={modal} toggle={toggle} backdrop={'static'} id="staticBackdrop">
                        <ModalHeader toggle={toggle} tag="h4">
                            {!!isEdit ? "Modifier - Fonctionnalité" : "Ajouter - Fonctionnalité"}
                        </ModalHeader>
                        <ModalBody>
                            <Form onSubmit={(e) => { e.preventDefault(); validation.handleSubmit(); return false; }}>
                                <Row>
                                    <Col className="col-12">
                                        <div className="mb-3">
                                            <Label>Nom</Label>
                                            <Input
                                                name="name"
                                                type="text"
                                                placeholder="Nom"
                                                onChange={validation.handleChange}
                                                onBlur={validation.handleBlur}
                                                value={validation.values.name || ""}
                                                invalid={validation.touched.name && validation.errors.name ? true : false}
                                            />
                                            {validation.touched.name && validation.errors.name ? (
                                                <FormFeedback type="invalid">{validation.errors.name}</FormFeedback>
                                            ) : null}
                                        </div>

                                        <div className="mb-3">
                                            <Label>Classe</Label>
                                            <Input
                                                name="code"
                                                type="select"
                                                className="form-select"
                                                onChange={validation.handleChange}
                                                onBlur={validation.handleBlur}
                                                value={validation.values.code || ""}
                                                invalid={validation.touched.code && validation.errors.code ? true : false}
                                            >
                                                <option value="">-- Sélectionner --</option>
                                                {FEATURES.map((c) => (
                                                    <option key={c.value} value={c.value}>{c.label}</option>
                                                ))}
                                            </Input>
                                            {validation.touched.code && validation.errors.code ? (
                                                <FormFeedback type="invalid">{validation.errors.code}</FormFeedback>
                                            ) : null}
                                        </div>

                                        

                                        <div className="mb-3">
                                            <Label>Actions associées</Label>
                                            <Select
                                                isMulti
                                                options={actionOptions}
                                                value={selectedActions}
                                                onChange={(vals) => setSelectedActions(vals || [])}
                                                placeholder="Sélectionner des actions..."
                                                noOptionsMessage={() => "Aucune action disponible"}
                                            />
                                            <small className="form-text text-muted">Sélectionnez les actions pour lesquelles des permissions seront créées.</small>
                                        </div>

                                        <div className="mb-3">
                                            <Label>Description</Label>
                                            <Input
                                                name="description"
                                                placeholder="Description"
                                                type="textarea"
                                                onChange={validation.handleChange}
                                                onBlur={validation.handleBlur}
                                                value={validation.values.description || ""}
                                            />
                                        </div>
                                    </Col>
                                </Row>

                                <Row>
                                    <Col>
                                        <div className="text-end">
                                            <Button color="primary" type="submit" className="save-user">
                                                {!!isEdit ? "Modifier" : "Enregistrer"}
                                            </Button>
                                        </div>
                                    </Col>
                                </Row>
                            </Form>
                        </ModalBody>
                    </Modal>
                </div>
            </div>
            <ToastContainer />
        </React.Fragment>
    );
}

export default Feature;
